import { useCallback, useEffect, type RefObject } from "react";
import type { HubConnection } from "@microsoft/signalr";
import { useLockStore } from "../../store/lockStore";
import { lockApi } from "../../api/lockApi";
import { asError, eventBus } from "@optimyl/events";

/**
 * `withAutomaticReconnect` rejoins the form's SignalR group but does NOT
 * replay messages sent during the disconnect window — those pushes are lost.
 * After a reconnect we read the server's authoritative lock state via
 * `acquireLock` (idempotent for the same session) and map it back to local
 * UI state. Five cases, by current lockStatus:
 *
 *   - locked + acquired=true  → in sync, no-op
 *   - locked + acquired=false → lock was lost during disconnect → "removed"
 *   - locked-by-other + acquired=true  → lock was released to us → take it
 *   - locked-by-other + acquired=false → maybe holder changed, refresh lockedBy
 *   - requesting + acquired=true  → request was granted to us → grant transition
 *   - requesting + acquired=false, status=Pending → still waiting, no-op
 *   - requesting + acquired=false, status!=Pending → request resolved
 *     (denied/cancelled/granted-to-someone-else) → drop back to lock screen.
 *     We surface "Denied" as a best-guess so the LockScreen shows the
 *     "still actively editing" message — the requester can click again.
 */
export function useReconcileOnReconnect(
  connection: HubConnection | null,
  formIdRef: RefObject<string | null>,
  sessionIdRef: RefObject<string>,
  onLockGrantedRef: RefObject<(() => Promise<void>) | undefined>,
  startGrantedTransition: () => void,
): void {
  const setLockStatus = useLockStore((s) => s.setLockStatus);
  const setOutgoingStatus = useLockStore((s) => s.setOutgoingStatus);
  const setCountdown = useLockStore((s) => s.setCountdown);

  const reconcile = useCallback(async () => {
    if (!formIdRef.current) return;
    try {
      const result = await lockApi.acquireLock(formIdRef.current, sessionIdRef.current);
      const state = useLockStore.getState();

      if (result.acquired) {
        if (state.lockStatus === "requesting") {
          setOutgoingStatus("Granted");
          startGrantedTransition();
        } else if (state.lockStatus === "locked-by-other") {
          // Lock was released during the disconnect; we got picked up. No 2s
          // overlay here — we never showed the waiting screen, so there's
          // nothing to transition out of dramatically.
          const reloadPromise = onLockGrantedRef.current
            ? onLockGrantedRef.current().catch((err) => {
                eventBus.publish("error", { error: asError(err), context: "useReconcileOnReconnect.reloadAfterReconnectAcquire" });
              })
            : Promise.resolve();
          await reloadPromise;
          setLockStatus("locked");
        }
        // locked → already in sync; removed → terminal, leave alone
      } else {
        if (state.lockStatus === "locked") {
          setLockStatus("removed", result.lockedBy);
        } else if (state.lockStatus === "requesting") {
          const stillPending = result.requestAccessStatus === "Pending";
          if (!stillPending) {
            setLockStatus("locked-by-other", result.lockedBy);
            setOutgoingStatus("Denied");
            setCountdown(null);
          }
        } else if (state.lockStatus === "locked-by-other") {
          if (result.lockedBy !== state.lockedBy) {
            setLockStatus("locked-by-other", result.lockedBy);
          }
        }
      }
    } catch (err) {
      eventBus.publish("error", { error: asError(err), context: "useReconcileOnReconnect" });
    }
  }, [setLockStatus, setOutgoingStatus, setCountdown, startGrantedTransition, formIdRef, sessionIdRef, onLockGrantedRef]);

  useEffect(() => {
    if (!connection) return;
    // SignalR HubConnection has no offreconnected() — handlers register, never
    // deregister. The connection.stop() that runs on useSignalR unmount stops
    // all events including reconnected. As long as we route through a stable
    // ref-reading handler (reconcile is a useCallback over store setters),
    // repeated registrations across effect re-runs are harmless.
    connection.onreconnected(() => { void reconcile(); });
  }, [connection, reconcile]);
}
